/**
 * PlanningEventDrawer — actions rapides sur un rendez-vous.
 * ──────────────────────────────────────────────────────────
 * Le user tape une carte du planning → drawer premium (sheet mobile,
 * modale desktop) qui expose UNE LISTE D'ACTIONS courtes :
 *
 *   • Ouvrir le dossier          (navigation)
 *   • Modifier le rendez-vous    (ouvre DeliverySlotsSheet)
 *   • Envoyer un message         (message inline direct)
 *   • Confirmer la livraison     (PATCH step livraison → termine)
 *
 * Pas de dix boutons. Chaque action a un icon + un label clair.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, ArrowRight, MessageCircle, Truck, CheckCircle2, Loader2, AlertCircle,
  User, Clock, MapPin, Send,
} from "lucide-react";
import { Card, Chip, MicroLabel } from "@/components/wyvio-ui";
import api from "@/lib/api";

const StatusChip = ({ event }) => {
  if (event.delivery_completed) {
    return <Chip tone="accent" size="sm"><CheckCircle2 size={10} strokeWidth={2.5} /> Livraison effectuée</Chip>;
  }
  if (event.payment_status === "confirmed") {
    return <Chip tone="accent" size="sm"><CheckCircle2 size={10} strokeWidth={2.5} /> Paiement validé</Chip>;
  }
  if (event.payment_status === "declared") {
    return <Chip tone="neutral" size="sm"><Loader2 size={10} strokeWidth={2.5} /> Paiement à confirmer</Chip>;
  }
  if (event.payment_status === "pending" || event.gate_blocked) {
    return <Chip tone="danger" size="sm"><AlertCircle size={10} strokeWidth={2.5} /> Paiement en attente</Chip>;
  }
  if (event.vehicle_ready) {
    return <Chip tone="accent" size="sm"><CheckCircle2 size={10} strokeWidth={2.5} /> Véhicule prêt</Chip>;
  }
  return <Chip tone="neutral" size="sm">{event.status_label}</Chip>;
};

const PlanningEventDrawer = ({
  event, onClose, onOpenDossier, onOpenSlots, onDone,
}) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(null); // 'msg' | 'confirm' | null
  const [error, setError] = useState("");
  const [okBanner, setOkBanner] = useState("");

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const sendMessage = async () => {
    if (!msg.trim() || busy) return;
    setBusy("msg"); setError("");
    try {
      await api.sendAgencyMessage(event.dossier_id, msg.trim());
      setOkBanner("Message envoyé");
      setMsg("");
      setTimeout(() => setOkBanner(""), 1500);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const confirmDelivery = async () => {
    if (busy) return;
    if (!confirm("Confirmer que la livraison a bien été effectuée ?")) return;
    setBusy("confirm"); setError("");
    try {
      await api.updateStep(event.dossier_id, "livraison", { status: "termine" });
      setOkBanner("Livraison confirmée");
      setTimeout(() => {
        onDone?.();
      }, 800);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div
      data-testid="planning-drawer"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div onClick={onClose} className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px]" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-[440px] bg-white dark:bg-[#111111] rounded-t-3xl sm:rounded-3xl border border-[#EEF2F5] dark:border-[#262626] p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280] dark:text-[#A3A3A3]">
              {event.time || "Livraison"}
            </p>
            <h3 className="mt-1 text-[22px] leading-[1.15] font-semibold tracking-[-0.025em] text-[#0A0A0A] dark:text-white truncate">
              {event.title}
            </h3>
            <p className="mt-1 text-[13px] text-[#6B7280] dark:text-[#A3A3A3] truncate">
              <User size={12} strokeWidth={2} className="inline mr-1 -mt-0.5" />
              {event.client_name || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            data-testid="planning-drawer-close"
            className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F7F8FA] dark:hover:bg-[#1A1A1A] shrink-0"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <StatusChip event={event} />
          {event.progress != null && (
            <Chip tone="neutral" size="sm">{event.progress}% du dossier</Chip>
          )}
        </div>

        {(event.location || event.commercial) && (
          <div className="mt-4 space-y-1.5 text-[12.5px] text-[#374151] dark:text-[#D4D4D4]">
            {event.location && (
              <p className="flex items-center gap-2">
                <MapPin size={13} strokeWidth={2} className="text-[#9CA3AF] shrink-0" />
                <span className="truncate">{event.location}</span>
              </p>
            )}
            {event.commercial && (
              <p className="flex items-center gap-2">
                <User size={13} strokeWidth={2} className="text-[#9CA3AF] shrink-0" />
                <span className="truncate">{event.commercial}</span>
              </p>
            )}
          </div>
        )}

        {/* Actions premium — une liste, pas dix boutons */}
        <div className="mt-6">
          <MicroLabel>Actions rapides</MicroLabel>
          <div className="mt-3 space-y-2">
            <ActionRow
              testId="planning-action-open"
              icon={ArrowRight}
              label="Ouvrir le dossier"
              subtitle="Voir toute la fiche véhicule"
              onClick={() => onOpenDossier(event.dossier_id)}
            />
            <ActionRow
              testId="planning-action-modify"
              icon={Truck}
              label="Modifier le rendez-vous"
              subtitle="Nouveaux créneaux ou nouvelle date"
              onClick={() => onOpenSlots(event.dossier_id)}
            />
            <ActionRow
              testId="planning-action-confirm"
              icon={CheckCircle2}
              label={event.delivery_completed ? "Livraison déjà effectuée" : "Confirmer la livraison"}
              subtitle={event.delivery_completed
                ? "Cette étape a déjà été validée."
                : "Marquer comme livrée maintenant"}
              onClick={confirmDelivery}
              disabled={busy === "confirm" || event.delivery_completed}
              accent={!event.delivery_completed}
            />
          </div>
        </div>

        {/* Envoi rapide de message */}
        {!event.delivery_completed && (
          <div className="mt-6">
            <MicroLabel>Envoyer un message au client</MicroLabel>
            <div className="mt-3 flex items-center gap-2">
              <input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                data-testid="planning-quick-message"
                placeholder="Bonjour, à demain pour la livraison !"
                className="flex-1 h-11 rounded-full border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 text-[13.5px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
              />
              <button
                onClick={sendMessage}
                disabled={!msg.trim() || busy === "msg"}
                data-testid="planning-quick-message-send"
                aria-label="Envoyer"
                className="h-11 w-11 rounded-full bg-[#0A0A0A] text-white dark:bg-white dark:text-[#0A0A0A] flex items-center justify-center hover:brightness-90 active:scale-[0.97] disabled:opacity-40 transition"
              >
                {busy === "msg" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} strokeWidth={2.25} />
                )}
              </button>
            </div>
          </div>
        )}

        {okBanner && (
          <p data-testid="planning-drawer-ok" className="mt-4 text-[12.5px] font-medium text-[#166534] dark:text-[#86EFAC]">
            {okBanner}
          </p>
        )}
        {error && (
          <p data-testid="planning-drawer-error" className="mt-4 text-[12.5px] font-medium text-[#991B1B] dark:text-[#FCA5A5]">
            {error}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
};

const ActionRow = ({ icon: Icon, label, subtitle, onClick, testId, disabled, accent }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={[
      "w-full text-left rounded-2xl border p-4 flex items-center gap-3 group",
      "transition-all duration-200 ease-out",
      disabled
        ? "border-[#EEF2F5] dark:border-[#262626] opacity-50 cursor-not-allowed"
        : accent
        ? "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#0A0A0A]/20 dark:hover:border-white/20 hover:-translate-y-0.5"
        : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
    ].join(" ")}
  >
    <div className={[
      "h-10 w-10 rounded-2xl flex items-center justify-center shrink-0",
      accent ? "bg-[#F3FCE1] dark:bg-[#1F2A0A] text-[#4a6b0f] dark:text-[#C8FF2F]" : "bg-[#F3F4F6] dark:bg-[#1A1A1A] text-[#0A0A0A] dark:text-white",
    ].join(" ")}>
      <Icon size={16} strokeWidth={2.25} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[14px] font-semibold text-[#0A0A0A] dark:text-white leading-tight">
        {label}
      </p>
      <p className="mt-0.5 text-[12px] text-[#6B7280] dark:text-[#A3A3A3] truncate">
        {subtitle}
      </p>
    </div>
    {!disabled && (
      <ArrowRight size={14} strokeWidth={2} className="text-[#9CA3AF] group-hover:text-[#0A0A0A] dark:group-hover:text-white transition-colors shrink-0" />
    )}
  </button>
);

export default PlanningEventDrawer;
export { StatusChip };
